import { forwardRef } from 'react';
import { Controller, Control, ControllerProps, FieldValues, Path, RegisterOptions } from 'react-hook-form';
import { withCleanTextRule } from '@/utils/sanitize';
import { shouldUseRTL } from '@/utils/rtl';

interface TextareaProps<T extends FieldValues> extends Omit<React.TextareaHTMLAttributes<HTMLTextAreaElement>, 'name'> {
  name: Path<T>;
  control: Control<T>;
  label?: string;
  error?: string;
  required?: boolean;
  helperText?: string;
  rules?: RegisterOptions;
  /** VAPT #12/#13 - set true for fields that legitimately need symbols (code, maths, questions). */
  allowSpecialChars?: boolean;
  /** Content positioned over the textarea (e.g. a character counter). */
  overlay?: React.ReactNode;
  /** Use only `className` for the textarea, without the default styles. */
  unstyled?: boolean;
  /** The textarea switches to right-to-left for Arabic etc.; set true to keep left-to-right. */
  disableRTL?: boolean;
}

function TextareaComponent<T extends FieldValues>(
  { name, control, label, error, required, helperText, className = '', rules, allowSpecialChars = false, overlay, unstyled = false, disableRTL = false, ...rest }: TextareaProps<T>,
  ref: any
) {
  return (
    <div className="w-full">
      {label && (
        <label htmlFor={name} className="block text-sm font-medium text-gray-700 mb-1">
          {label}
          {required && <span className="text-red-500 ml-1">*</span>}
        </label>
      )}
      <Controller
        name={name}
        control={control}
        rules={withCleanTextRule(rules, !allowSpecialChars) as unknown as ControllerProps<T, Path<T>>['rules']}
        render={({ field, fieldState }) => {
          // Fall back to the field's own error so a failed check is shown even if the page passes no `error`.
          const message = error ?? fieldState.error?.message;
          // Same detection as RTLTextarea: >= 30% right-to-left characters flips direction
          const is_rtl = !disableRTL && typeof field.value === 'string' && shouldUseRTL(field.value, 30);
          return (
            <>
              <div className="relative">
                <textarea
                  {...rest}
                  {...field}
                  ref={ref}
                  id={name}
                  dir={is_rtl ? 'rtl' : rest.dir}
                  style={is_rtl ? { ...rest.style, direction: 'rtl', textAlign: 'right' } : rest.style}
                  className={unstyled ? className : `w-full px-3 sm:px-4 py-2 sm:py-3 text-xs sm:text-sm border rounded-lg sm:rounded-xl focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-all duration-200 resize-y ${
                    message ? 'border-red-500 focus:ring-red-500' : 'border-gray-200'
                  } ${className}`}
                />
                {overlay}
              </div>
              {message && <p className="text-red-500 text-xs mt-1">{message}</p>}
              {helperText && !message && <p className="text-gray-500 text-xs mt-1">{helperText}</p>}
            </>
          );
        }}
      />
    </div>
  );
}

export const Textarea = forwardRef(TextareaComponent) as <T extends FieldValues>(
  props: TextareaProps<T> & { ref?: any }
) => ReturnType<typeof TextareaComponent>;
